import * as React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Search } from "lucide-react";
import {
  Avatar,
  Boton,
  Campo,
  Dialog,
  DialogContenido,
  DialogDescripcion,
  DialogEncabezado,
  DialogPie,
  DialogTitulo,
  EsqueletoTabla,
  Input,
  Pill,
  Select,
  SelectContenido,
  SelectDisparador,
  SelectOpcion,
  SelectValor,
  Tabla,
  TablaCabecera,
  TablaCelda,
  TablaCuerpo,
  TablaEncabezado,
  TablaFila,
  Textarea,
  TituloPagina,
  Vacio,
  cn,
  useToast,
} from "@iavmx/ui";
import { Chip, Mensaje, Segmento } from "../componentes";
import { leer } from "../api";
import { useSesion } from "../sesion";
import { dominioPermitido } from "@iavmx/auth/dominios";
import { fechaCorta, relativa } from "../fechas";
import type { Area, PersonaAdmin, RespuestaAdmin, ResultadoInvitacion } from "../tipos";
import { APPS_INVITABLES, accesosSugeridos, nombreRolApp, ROLES_POR_APP, type ClaveApp } from "../../compartido/apps";

const SIN = "__sin__";
const NOMBRE_APP: Record<string, string> = { coordinacion: "360 y Semáforo", comercial: "Comercial" };
type Filtro = "todas" | "activas" | "sin_aceptar" | "desactivadas";

export function Admin() {
  const { api, yo, configuracion } = useSesion();
  const qc = useQueryClient();
  const { avisar } = useToast();
  const q = useQuery({ queryKey: ["admin"], queryFn: () => api.api.admin.personas.$get().then((r) => leer<RespuestaAdmin>(r)) });
  const areas = useQuery({ queryKey: ["areas"], queryFn: () => api.api.areas.$get().then((r) => leer<{ areas: Area[] }>(r)) });
  const [modo, setModo] = React.useState<"una" | "lista">("una");
  const [correo, setCorreo] = React.useState("");
  const [nombre, setNombre] = React.useState("");
  const [texto, setTexto] = React.useState("");
  const [areaId, setAreaId] = React.useState<string>(SIN);
  const [accesos, setAccesos] = React.useState<Record<string, string>>({ coordinacion: "solicitante", comercial: "" });
  const [admin, setAdmin] = React.useState(false);
  const [resultados, setResultados] = React.useState<ResultadoInvitacion[] | null>(null);
  const [filtro, setFiltro] = React.useState<Filtro>("todas");
  const [busqueda, setBusqueda] = React.useState("");
  const [aDesactivar, setADesactivar] = React.useState<PersonaAdmin | null>(null);
  const [aEditar, setAEditar] = React.useState<PersonaAdmin | null>(null);

  const refrescar = () => {
    void qc.invalidateQueries({ queryKey: ["admin"] });
    void qc.invalidateQueries({ queryKey: ["directorio"] });
  };
  const invitar = useMutation({
    mutationFn: () =>
      api.api.admin.invitaciones
        .$post({
          json: {
            ...(modo === "una" ? { invitaciones: [{ correo, nombre: nombre || undefined }] } : { texto }),
            areaId: areaId === SIN ? null : areaId,
            accesos: Object.fromEntries(Object.entries(accesos).filter(([, v]) => v)),
            admin,
          },
        })
        .then((r) => leer<{ resultados: ResultadoInvitacion[]; enviadas: number; registradas: number; rechazadas: number }>(r)),
    onSuccess: (r) => {
      setResultados(r.resultados);
      if (r.enviadas + r.registradas > 0) {
        setCorreo(""); setNombre(""); setTexto("");
        avisar({ titulo: r.enviadas ? `${r.enviadas} ${r.enviadas === 1 ? "invitación enviada" : "invitaciones enviadas"}` : `${r.registradas} ${r.registradas === 1 ? "persona registrada" : "personas registradas"} sin correo`, tono: "exito" });
      }
      refrescar();
    },
    onError: (e) => avisar({ titulo: "No se pudo invitar", descripcion: e.message, tono: "error" }),
  });
  const reenviar = useMutation({
    mutationFn: (id: string) => api.api.admin.personas[":id"].reenviar.$post({ param: { id } }).then((r) => leer<{ enviada: boolean; motivo?: string }>(r)),
    onSuccess: (r) => { avisar(r.enviada ? { titulo: "Invitación reenviada", tono: "exito" } : { titulo: "No se mandó el correo", descripcion: r.motivo, tono: "error" }); refrescar(); },
    onError: (e) => avisar({ titulo: "No se pudo reenviar", descripcion: e.message, tono: "error" }),
  });
  const desactivar = useMutation({
    mutationFn: (id: string) => api.api.admin.personas[":id"].desactivar.$post({ param: { id } }).then((r) => leer<{ nombre: string }>(r)),
    onSuccess: (r) => { setADesactivar(null); avisar({ titulo: `${r.nombre} ya no puede entrar`, descripcion: "Puedes reactivar cuando quieras.", tono: "neutro" }); refrescar(); },
    onError: (e) => avisar({ titulo: "No se pudo desactivar", descripcion: e.message, tono: "error" }),
  });
  const reactivar = useMutation({
    mutationFn: (id: string) => api.api.admin.personas[":id"].reactivar.$post({ param: { id } }).then((r) => leer<{ nombre: string }>(r)),
    onSuccess: (r) => { avisar({ titulo: `${r.nombre} vuelve a tener acceso`, tono: "exito" }); refrescar(); },
    onError: (e) => avisar({ titulo: "No se pudo reactivar", descripcion: e.message, tono: "error" }),
  });
  const guardarAccesos = useMutation({
    mutationFn: (p: { id: string; areaId: string | null; accesos: Record<string, string>; admin: boolean }) =>
      api.api.admin.personas[":id"].accesos.$put({ param: { id: p.id }, json: { areaId: p.areaId, accesos: Object.fromEntries(Object.entries(p.accesos).filter(([, v]) => v)), admin: p.admin } }).then((r) => leer(r)),
    onSuccess: () => { setAEditar(null); avisar({ titulo: "Accesos actualizados", descripcion: "Se aplican en el momento; la persona no tiene que volver a entrar.", tono: "exito" }); refrescar(); },
    onError: (e) => avisar({ titulo: "No se guardó", descripcion: e.message, tono: "error" }),
  });

  if (!yo?.esAdmin) return <Vacio titulo="Esto es de administración" texto="Tu rol no alcanza para invitar ni desactivar personas." />;
  if (q.isLoading) return <EsqueletoTabla />;
  if (q.error || !q.data) return <Vacio titulo="No se pudo cargar" texto={q.error?.message} />;
  const d = q.data;
  const disponibles = d.invitacionesDisponibles;
  const termino = busqueda.trim().toLowerCase();
  const lista = d.personas.filter((p) => {
    if (filtro === "activas" && p.estado !== "activa") return false;
    if (filtro === "sin_aceptar" && p.estado !== "sin_aceptar" && p.estado !== "vencida") return false;
    if (filtro === "desactivadas" && p.estado !== "desactivada") return false;
    return !termino || `${p.nombre} ${p.correo} ${p.areaNombre ?? ""}`.toLowerCase().includes(termino);
  });
  // La misma muralla que el Worker: vanguardia.com.mx más los dominios extra del entorno.
  const extra = configuracion?.dominiosExtra ?? [];
  const lineasConCorreo = modo === "lista" ? texto.split(/\n/).map((l) => l.trim()).filter((l) => l.includes("@")) : [];
  const rechazadasLocal = lineasConCorreo.filter((l) => !(l.match(/[^\s,;<>]+@[^\s,;<>]+/g) ?? []).some((c) => dominioPermitido(c, extra)));
  const cuantas = modo === "una" ? (correo.trim() ? 1 : 0) : lineasConCorreo.length - rechazadasLocal.length;
  const sugerir = (id: string) => {
    const clave = areas.data?.areas.find((a) => a.id === id)?.clave ?? null;
    const s = accesosSugeridos(clave);
    setAccesos({ coordinacion: s.coordinacion, comercial: s.comercial });
  };

  return (
    <>
      <TituloPagina descripcion={`${d.conteos.activas} activas · ${d.conteos.sinAceptar} ${d.conteos.sinAceptar === 1 ? "invitación sin aceptar" : "invitaciones sin aceptar"} · ${d.conteos.desactivadas} ${d.conteos.desactivadas === 1 ? "desactivada" : "desactivadas"} · solo lo ven los administradores`}>
        Personas y accesos
      </TituloPagina>
      <div className="grid grid-cols-[minmax(0,1fr)] gap-10 xl:grid-cols-[520px_minmax(0,1fr)]">
        <form
          className="min-w-0 space-y-4 bg-fondo-suave p-5"
          onSubmit={(e) => { e.preventDefault(); setResultados(null); invitar.mutate(); }}
          noValidate
          aria-labelledby="invitar"
        >
          <h2 id="invitar" className="font-titulo text-xl font-bold">Invitar</h2>
          {!disponibles && (
            <Mensaje tono="info" titulo="Invitaciones deshabilitadas en este entorno." className="[overflow-wrap:anywhere]">
              Falta la llave de servicio de Supabase (SUPABASE_SERVICE_ROLE_KEY). Puedes registrar personas en el directorio; el correo con la liga no se manda.
            </Mensaje>
          )}
          <Segmento etiqueta="Modo de invitación" valor={modo} onCambio={setModo} opciones={[{ valor: "una", nombre: "Una persona" }, { valor: "lista", nombre: "Pegar una lista" }]} />
          {modo === "una" ? (
            <div className="grid gap-4 sm:grid-cols-2">
              <Campo etiqueta="Correo de Vanguardia" htmlFor="inv-correo">
                <Input id="inv-correo" type="email" inputMode="email" className="h-11 text-base" placeholder="nombre@vanguardia.com.mx" value={correo} onChange={(e) => setCorreo(e.target.value)} required />
              </Campo>
              <Campo etiqueta="Nombre" htmlFor="inv-nombre" ayuda="Si lo dejas vacío, se toma del correo.">
                <Input id="inv-nombre" className="h-11 text-base" value={nombre} onChange={(e) => setNombre(e.target.value)} autoComplete="off" />
              </Campo>
            </div>
          ) : (
            <Campo etiqueta="Correos de Vanguardia" htmlFor="inv-lista" ayuda="Una por línea: correo, nombre, área (nombre y área son opcionales). Solo se invitan cuentas @vanguardia.com.mx.">
              <Textarea id="inv-lista" className="min-h-32 text-base" value={texto} onChange={(e) => setTexto(e.target.value)} placeholder={"mariana.ruiz@vanguardia.com.mx, Mariana Ruiz, Mercadotecnia\nricardo.torres@vanguardia.com.mx"} />
            </Campo>
          )}
          {rechazadasLocal.length > 0 && (
            <Mensaje tono="error" titulo={`${rechazadasLocal.length} ${rechazadasLocal.length === 1 ? "correo no es" : "correos no son"} de Vanguardia y no se va a enviar.`}>
              {rechazadasLocal.join(", ")} · corrígelo o quítalo de la lista.
            </Mensaje>
          )}
          <Campo etiqueta={cuantas > 1 ? `Área para las ${cuantas} personas` : "Área"} htmlFor="inv-area" ayuda="Si una línea de la lista trae área, manda la de la línea.">
            <Select value={areaId} onValueChange={(v) => { setAreaId(v); if (v !== SIN) sugerir(v); }}>
              <SelectDisparador id="inv-area" className="h-11 text-base"><SelectValor placeholder="Elige un área" /></SelectDisparador>
              <SelectContenido>
                <SelectOpcion value={SIN}>Sin área por ahora</SelectOpcion>
                {areas.data?.areas.map((a) => <SelectOpcion key={a.id} value={a.id}>{a.nombre}</SelectOpcion>)}
              </SelectContenido>
            </Select>
          </Campo>
          <fieldset className="space-y-2">
            <legend className="font-ui text-xs font-semibold uppercase tracking-wide text-tinta-3">Rol por herramienta</legend>
            <SelectorAccesos valor={accesos} onCambio={setAccesos} />
            <label className="flex min-h-11 items-center gap-2 font-ui text-sm">
              <input type="checkbox" className="size-4 accent-tinta" checked={admin} onChange={(e) => setAdmin(e.target.checked)} />
              <span>Administración <span className="text-tinta-tenue">· puede invitar y desactivar personas</span></span>
            </label>
          </fieldset>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <span className="font-ui text-xs text-tinta-tenue">{disponibles ? "Cada quien recibe un correo con una liga que sirve una sola vez." : "Se registran en el directorio; el correo no se manda en este entorno."}</span>
            <Boton type="submit" variante="acento" className="h-11 px-5 text-tinta" disabled={invitar.isPending || cuantas === 0}>
              {invitar.isPending ? "Enviando…" : disponibles ? (cuantas > 1 ? `Enviar ${cuantas} invitaciones` : "Enviar invitación") : (cuantas > 1 ? `Registrar ${cuantas} personas sin correo` : "Registrar sin enviar correo")}
            </Boton>
          </div>
          {resultados && (
            <ul className="divide-y divide-linea border-y border-linea font-ui text-sm" aria-live="polite">
              {resultados.map((r, i) => (
                <li key={i} className="flex flex-wrap items-center justify-between gap-2 py-2">
                  <span className="min-w-0 truncate">{r.correo}{r.nombre && <span className="text-tinta-tenue"> · {r.nombre}</span>}</span>
                  <span className={cn("shrink-0", r.resultado === "rechazada" ? "text-rojo" : r.resultado === "enviada" ? "text-verde" : "text-tinta-2")}>
                    {r.resultado === "enviada" ? "Invitación enviada" : r.resultado === "registrada" ? `Registrada · ${r.motivo ?? "sin correo"}` : r.motivo}
                  </span>
                </li>
              ))}
            </ul>
          )}
        </form>

        <section className="min-w-0 space-y-4" aria-labelledby="lista-t">
          <h2 id="lista-t" className="sr-only">Personas</h2>
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Filtrar personas">
              <Chip activo={filtro === "todas"} onClick={() => setFiltro("todas")} n={d.conteos.todas}>Todas</Chip>
              <Chip activo={filtro === "activas"} onClick={() => setFiltro("activas")} n={d.conteos.activas}>Activas</Chip>
              <Chip activo={filtro === "sin_aceptar"} onClick={() => setFiltro("sin_aceptar")} n={d.conteos.sinAceptar}>Sin aceptar</Chip>
              <Chip activo={filtro === "desactivadas"} onClick={() => setFiltro("desactivadas")} n={d.conteos.desactivadas}>Desactivadas</Chip>
            </div>
            <label className="relative w-full sm:w-64">
              <span className="sr-only">Buscar persona</span>
              <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-tinta-tenue" aria-hidden="true" />
              <Input type="search" placeholder="Buscar persona" className="h-11 pl-9 text-base" value={busqueda} onChange={(e) => setBusqueda(e.target.value)} />
            </label>
          </div>
          {lista.length === 0 ? (
            <Vacio titulo="Sin coincidencias" texto="Cambia el filtro o la búsqueda." />
          ) : (
            <Tabla>
              <TablaCabecera>
                <TablaFila>
                  <TablaEncabezado>Persona</TablaEncabezado>
                  <TablaEncabezado>Área</TablaEncabezado>
                  <TablaEncabezado>Herramientas</TablaEncabezado>
                  <TablaEncabezado>Estado</TablaEncabezado>
                  <TablaEncabezado><span className="sr-only">Acciones</span></TablaEncabezado>
                </TablaFila>
              </TablaCabecera>
              <TablaCuerpo>
                {lista.map((p) => (
                  <TablaFila key={p.id} className={cn(p.estado === "desactivada" && "bg-fondo-suave text-tinta-tenue")}>
                    <TablaCelda>
                      <span className="flex items-center gap-2.5">
                        <Avatar nombre={p.nombre} src={p.fotoUrl ?? p.avatarUrl} className="size-8" />
                        <span className="min-w-0">
                          <span className="block font-semibold">{p.nombre}{p.rol === "admin" && <Pill tono="tinta" className="ml-2">Admin</Pill>}</span>
                          <span className="block text-xs text-tinta-tenue">{p.correo}{p.invitadoPorNombre && ` · ${p.invitadoPorNombre === yo.nombre ? "invitada por ti" : `invitó ${p.invitadoPorNombre}`}`}</span>
                        </span>
                      </span>
                    </TablaCelda>
                    <TablaCelda>{p.areaNombre ?? "—"}</TablaCelda>
                    <TablaCelda>
                      <span className="flex flex-wrap gap-1.5">
                        {p.accesos.map((a) => <Pill key={a.app} tono="neutro">{NOMBRE_APP[a.app] ?? a.app} · {nombreRolApp(a.app as ClaveApp, a.rol)}</Pill>)}
                        {p.accesos.length === 0 && <span className="text-tinta-tenue">—</span>}
                      </span>
                    </TablaCelda>
                    <TablaCelda>
                      <EstadoPill p={p} />
                      <span className="block text-xs text-tinta-tenue">
                        {p.estado === "activa" && p.ultimoAccesoEn ? `Entró ${relativa(p.ultimoAccesoEn)}` : p.estado === "activa" ? "Todavía no entra" : p.estado === "desactivada" && p.desactivadaEn ? `Desde ${fechaCorta(p.desactivadaEn)}` : p.invitadoEn ? `Enviada ${relativa(p.invitadoEn)}` : ""}
                        {(p.estado === "sin_aceptar" || p.estado === "vencida") && (
                          <> · <button type="button" className="inline-flex min-h-11 items-center text-azul underline-offset-4 hover:underline disabled:opacity-50 sm:min-h-0" onClick={() => reenviar.mutate(p.id)} disabled={reenviar.isPending || !disponibles} title={disponibles ? undefined : "Invitaciones deshabilitadas en este entorno"}>Reenviar</button></>
                        )}
                      </span>
                    </TablaCelda>
                    <TablaCelda className="whitespace-nowrap text-right">
                      {p.estado === "desactivada" ? (
                        <Boton variante="liga" tamano="sm" className="min-h-11 sm:min-h-0" onClick={() => reactivar.mutate(p.id)} disabled={reactivar.isPending}>Reactivar</Boton>
                      ) : (
                        <span className="inline-flex gap-3">
                          <Boton variante="liga" tamano="sm" className="min-h-11 sm:min-h-0" onClick={() => setAEditar(p)}>Editar</Boton>
                          {p.id !== yo.id && <Boton variante="liga" tamano="sm" className="min-h-11 text-rojo sm:min-h-0" onClick={() => setADesactivar(p)}>Desactivar</Boton>}
                        </span>
                      )}
                    </TablaCelda>
                  </TablaFila>
                ))}
              </TablaCuerpo>
            </Tabla>
          )}
          <p className="font-ui text-xs text-tinta-tenue">Mostrando {lista.length} de {d.conteos.todas} · Los cambios de rol se aplican en el momento; la persona no tiene que volver a entrar.</p>
        </section>
      </div>

      <Dialog open={Boolean(aDesactivar)} onOpenChange={(o) => !o && setADesactivar(null)}>
        <DialogContenido>
          <DialogEncabezado>
            <DialogTitulo>¿Desactivar a {aDesactivar?.nombre}?</DialogTitulo>
            <DialogDescripcion>Deja de entrar a todas las herramientas desde ahora. Sus solicitudes, tareas y cotizaciones se conservan. Puedes reactivar cuando quieras.</DialogDescripcion>
          </DialogEncabezado>
          <DialogPie>
            <Boton variante="fantasma" onClick={() => setADesactivar(null)}>Cancelar</Boton>
            <Boton variante="primario" onClick={() => aDesactivar && desactivar.mutate(aDesactivar.id)} disabled={desactivar.isPending}>{desactivar.isPending ? "Desactivando…" : "Desactivar"}</Boton>
          </DialogPie>
        </DialogContenido>
      </Dialog>

      {aEditar && (
        <EditarAccesos persona={aEditar} areas={areas.data?.areas ?? []} esYo={aEditar.id === yo.id} guardando={guardarAccesos.isPending} onCerrar={() => setAEditar(null)} onGuardar={(v) => guardarAccesos.mutate({ id: aEditar.id, ...v })} />
      )}
    </>
  );
}

function EstadoPill({ p }: { p: PersonaAdmin }) {
  if (p.estado === "activa") return <Pill tono="verde">Activa</Pill>;
  if (p.estado === "sin_aceptar") return <Pill tono="azul">Sin aceptar</Pill>;
  if (p.estado === "vencida") return <Pill tono="ambar">Invitación vencida</Pill>;
  return <Pill tono="neutro">Desactivada</Pill>;
}

function SelectorAccesos({ valor, onCambio }: { valor: Record<string, string>; onCambio: (v: Record<string, string>) => void }) {
  return (
    <div className="grid gap-2 sm:grid-cols-[140px_1fr] sm:items-center">
      {APPS_INVITABLES.map((app) => (
        <React.Fragment key={app}>
          <label htmlFor={`acc-${app}`} className="font-ui text-sm">{NOMBRE_APP[app]}</label>
          <Select value={valor[app] || SIN} onValueChange={(v) => onCambio({ ...valor, [app]: v === SIN ? "" : v })}>
            <SelectDisparador id={`acc-${app}`} className={cn("h-11 text-base", !valor[app] && "text-tinta-tenue")}><SelectValor /></SelectDisparador>
            <SelectContenido>
              <SelectOpcion value={SIN}>Sin acceso</SelectOpcion>
              {ROLES_POR_APP[app].map((r) => <SelectOpcion key={r.clave} value={r.clave}>{r.nombre}</SelectOpcion>)}
            </SelectContenido>
          </Select>
        </React.Fragment>
      ))}
    </div>
  );
}

function EditarAccesos({ persona, areas, esYo, guardando, onCerrar, onGuardar }: { persona: PersonaAdmin; areas: Area[]; esYo: boolean; guardando: boolean; onCerrar: () => void; onGuardar: (v: { areaId: string | null; accesos: Record<string, string>; admin: boolean }) => void }) {
  const [areaId, setAreaId] = React.useState(persona.areaId ?? SIN);
  const [accesos, setAccesos] = React.useState<Record<string, string>>(Object.fromEntries(APPS_INVITABLES.map((a) => [a, persona.accesos.find((x) => x.app === a)?.rol ?? ""])));
  const [admin, setAdmin] = React.useState(persona.rol === "admin");
  return (
    <Dialog open onOpenChange={(o) => !o && onCerrar()}>
      <DialogContenido>
        <DialogEncabezado>
          <DialogTitulo>Accesos de {persona.nombre}</DialogTitulo>
          <DialogDescripcion>{persona.correo}. Los cambios se aplican en el momento.</DialogDescripcion>
        </DialogEncabezado>
        <Campo etiqueta="Área" htmlFor="ed-area">
          <Select value={areaId} onValueChange={setAreaId}>
            <SelectDisparador id="ed-area" className="h-11 text-base"><SelectValor placeholder="Elige un área" /></SelectDisparador>
            <SelectContenido>
              <SelectOpcion value={SIN}>Sin área</SelectOpcion>
              {areas.map((a) => <SelectOpcion key={a.id} value={a.id}>{a.nombre}</SelectOpcion>)}
            </SelectContenido>
          </Select>
        </Campo>
        <SelectorAccesos valor={accesos} onCambio={setAccesos} />
        <label className="flex min-h-11 items-center gap-2 font-ui text-sm">
          <input type="checkbox" className="size-4 accent-tinta" checked={admin} disabled={esYo} onChange={(e) => setAdmin(e.target.checked)} />
          <span>Administración{esYo && <span className="text-tinta-tenue"> · no puedes quitártela a ti</span>}</span>
        </label>
        <DialogPie>
          <Boton variante="fantasma" onClick={onCerrar}>Cancelar</Boton>
          <Boton variante="primario" disabled={guardando} onClick={() => onGuardar({ areaId: areaId === SIN ? null : areaId, accesos, admin })}>{guardando ? "Guardando…" : "Guardar"}</Boton>
        </DialogPie>
      </DialogContenido>
    </Dialog>
  );
}
